import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { Paper, Typography, Box, Grid, Divider } from '@mui/material';
import Card from '../components/common/Card';
import useAnalysisStore from '../store/analysisStore';
import { parseQuestionData } from '../utils/questionData';
import PageHeader from '../components/common/PageHeader';
import { PieChart, BarChart } from '../components/charts';
import AsyncState from '../components/common/AsyncState';
import { useColorConfig } from '../utils/colorConfig';
import { useStatusColor } from '../utils/statusColors';
import SecureImage from '../components/common/SecureImage';
import { API_ENDPOINTS } from '../endpoints';
import ImageZoomDialog from '../components/common/ImageZoomDialog';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import useOptionConfig from '../hooks/useOptionConfig';

const QuestionnaireAnalysisPage = () => {
  const { id } = useParams();
  const {
    analysisDetail,
    detailLoading,
    detailError,
    fetchQuestionnaireAnalysis,
    showHint, setShowHint,
    showAnswers, setShowAnswers,
    topN, setTopN,
    clearAnalysisDetail,
    questionnaires,
    zoomImage, setZoomImage,
  } = useAnalysisStore();
  const colors = useColorConfig();
  const getStatusColor = useStatusColor();
  const { config } = useOptionConfig();
  const labels = config?.labels?.questionnaireAnalysisPage || {};
  const questionnaireName = useMemo(() => {
    const currentQ = questionnaires.find(q => String(q.id) === String(id));
    return currentQ?.code || 'Test';
  }, [questionnaires, id]);

  useEffect(() => {
    fetchQuestionnaireAnalysis(id);
    return () => { clearAnalysisDetail(); };
  }, [id, fetchQuestionnaireAnalysis, clearAnalysisDetail]);

  const statusCounts = useMemo(() => {
    const counts = {};
    analysisDetail.forEach((item) => {
      (item.userStat || []).forEach((u) => {
        const key = u.status || 'unknown';
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }, [analysisDetail]);

  const pieData = useMemo(
    () =>
      Object.entries(statusCounts).map(([label, value]) => ({ label, value, color: getStatusColor(label), })),
    [statusCounts, getStatusColor]
  );

  const topUsersData = useMemo(() => {
    const userCorrectCounts = {};
    (analysisDetail || []).forEach(item => {
      const { answerKey: qAnswerKey } = parseQuestionData(item.question);
      const correctAnswers = qAnswerKey.split('|').map(code => code.trim());
      (item.userStat || []).forEach(userStat => {
        const userId = userStat.email;
        const userName = `${userStat.fname} ${userStat.lname}`.trim() ? `${userStat.fname} ${userStat.lname} (${userStat.email})` : userStat.email;
        let isCorrect = false;
        if (userStat.status === 'right') {
          isCorrect = true;
        }
        else if (userStat.answer) {
          const userAnswer = userStat.answer.split('|').map(code => code.trim());
          isCorrect = correctAnswers.every(code => userAnswer.includes(code)) && userAnswer.every(code => correctAnswers.includes(code));
        }
        if (isCorrect) {
          userCorrectCounts[userId] = userCorrectCounts[userId] || { name: userName, correctCount: 0 };
          userCorrectCounts[userId].correctCount++;
        }
      });
    });

    const sortedUsers = Object.values(userCorrectCounts)
      .sort((a, b) => b.correctCount - a.correctCount)
      .slice(0, topN);
    return sortedUsers.map(user => ({ label: user.name, value: user.correctCount, }));
  }, [analysisDetail, topN]);

  return (
    <Layout>
      <PageHeader title={`Analytics Overview - ${questionnaireName} (${id})`} />
      <AsyncState loading={detailLoading} error={detailError} isEmpty={!detailLoading && analysisDetail.length === 0}>
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, mb: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>{labels.statusBreakdown || "Status Breakdown"}</Typography>
                    <PieChart data={pieData} title={labels.userStatus || "User Status"} />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, mb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>{labels.topUsersTitle || "Top Users with Correct Answers"}</Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <BarChart data={topUsersData} />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Input
                        label={labels.topNLabel || "Top N Users"}
                        type="number"
                        value={topN}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTopN(val === '' ? '' : parseInt(val, 10));
                        }}
                        inputProps={{ min: 0, step: 1 }}
                        size="small"
                        sx={{ width: 120 }}
                        margin="none"
                        fullWidth={false}
                      />
                      <Button variant="outlined" onClick={() => { setShowHint(!showHint); }}>
                        {showHint ? (labels.hideHint || 'Hide Hint') : (labels.showHint || 'Show Hint')}
                      </Button>
                      <Button variant="outlined" onClick={() => { setShowAnswers(!showAnswers); }}>
                        {showAnswers ? (labels.hideAnswers || 'Hide Answers') : (labels.showAnswers || 'Show Answers')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            {analysisDetail.map((item) => {
              const { options, type, answerKey } = parseQuestionData(item.question);

              let questionImages = [];
              try {
                if (item.question.questionImages) {
                  const parsed = JSON.parse(item.question.questionImages);
                  questionImages = Array.isArray(parsed) ? parsed : [];
                }
              } catch (e) {
                console.error("Failed to parse question images", e);
              }

              return (
                <Grid item xs={12} md={6} key={item.question.id} sx={{ display: 'flex' }}>
                  <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                      {labels.idLabel || "ID:"} {item.question.id}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      {item.question.text}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{labels.typeLabel || "Type:"} {type}</Typography>

                    {/* Question Images */}
                    {questionImages.length > 0 && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{labels.questionImages || "Question Images"}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {questionImages.map((img, i) => (
                            <Paper
                              key={i}
                              elevation={2}
                              onClick={() => setZoomImage(API_ENDPOINTS.getQaImages(img))}
                              sx={{
                                p: 0.5,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid #eee',
                                cursor: 'pointer'
                              }}
                            >
                              <SecureImage
                                url={API_ENDPOINTS.getQaImages(img)}
                                alt={`Question Image ${i + 1}`}
                                sx={{ maxWidth: 150, maxHeight: 150, objectFit: 'contain', display: 'block' }}
                              />
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Grid container spacing={2} sx={{ pl: 2, mb: 1 }}>
                      {options.map((opt, idx) => {
                        const imageKey = `option${opt.code}Images`;
                        const optImages = opt[imageKey];

                        return (
                          <Grid item xs={12} sm={6} key={opt.code || idx}>
                            <Box sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.primary }} />
                                <Typography variant="body2" color="text.secondary">
                                  {opt.code}. {opt.desc}
                                </Typography>
                              </Box>

                              {/* Option Images */}
                              {Array.isArray(optImages) && optImages.length > 0 && (
                                <Box sx={{ mt: 1, ml: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {optImages.map((img, i) => (
                                    <Paper
                                      key={i}
                                      elevation={1}
                                      onClick={() => setZoomImage(API_ENDPOINTS.getQaImages(img))}
                                      sx={{
                                        p: 0.5,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid #eee',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <SecureImage
                                        url={API_ENDPOINTS.getQaImages(img)}
                                        alt={`Option ${opt.code} Image ${i + 1}`}
                                        sx={{ maxWidth: 100, maxHeight: 100, objectFit: 'contain', display: 'block' }}
                                      />
                                    </Paper>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    {showHint && (
                      <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                        {labels.hintLabel || "Hint:"} {item.question.hint || (labels.noHint || 'No hint')}
                      </Typography>
                    )}
                    {showAnswers && answerKey && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {labels.answerLabel || "Answer:"}
                        </Typography>
                        <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {(() => {
                            const answerCodes = answerKey.split('|').map(code => code.trim());
                            const matchingOptions = options.filter(opt => answerCodes.includes(opt.code));
                            return matchingOptions.map((opt, index) => {
                              const imageKey = `option${opt.code}Images`;
                              const optImages = opt[imageKey];

                              return (
                                <Box key={index}>
                                  <Typography variant="body2" color="success.main">
                                    {opt.code}. {opt.desc}
                                  </Typography>

                                  {/* Answer Option Images */}
                                  {Array.isArray(optImages) && optImages.length > 0 && (
                                    <Box sx={{ mt: 1, ml: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {optImages.map((img, i) => (
                                        <Paper
                                          key={i}
                                          elevation={2}
                                          onClick={() => setZoomImage(API_ENDPOINTS.getQaImages(img))}
                                          sx={{
                                            p: 0.5,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '2px solid',
                                            borderColor: 'success.main',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <SecureImage
                                            url={API_ENDPOINTS.getQaImages(img)}
                                            alt={`Answer ${opt.code} Image ${i + 1}`}
                                            sx={{ maxWidth: 120, maxHeight: 120, objectFit: 'contain', display: 'block' }}
                                          />
                                        </Paper>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" fontWeight={600} gutterBottom>{labels.usersTitle || "Users"}</Typography>
                    {(item.userStat || []).map((u) => (
                      <Box key={u.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{u.fname} {u.lname} ({u.email})</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: getStatusColor(u.status) }} />
                        </Box>
                      </Box>
                    ))}
                    {(!item.userStat || item.userStat.length === 0) && (
                      <Typography color="text.secondary">{labels.noUserStats || "No user stats."}</Typography>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      </AsyncState>
      <ImageZoomDialog open={!!zoomImage} imageUrl={zoomImage} onClose={() => setZoomImage(null)} />
    </Layout>
  );
};

export default QuestionnaireAnalysisPage;